// src/screens/VerbPractice.jsx
import React, { useEffect, useState } from "react";
import "../App.css";
import { supabase } from "../supabaseClient";
import { loadConjugations } from "../data/loadConjugations";
import SpeakButton from "../components/SpeakButton";
import { verbFormForSpeech } from "../utils/speechText";
import {
  PERSON_SLOTS,
  pronounFor,
  pronounForForm,
  parseLanguageId,
} from "../data/languages";
import { TARGET_WORDS, PACE_YEARS } from "../data/learningConfig";
import {
  loadVerbProgress,
  saveVerbProgress,
  ensureVerbStatesForWeeklySet,
} from "../data/verbProgressStorage";
import { getAllowedTenses } from "../data/tenseSchedule";
import {
  buildVerbTasks,
  slotsForForms,
  acceptedVerbForms,
  gradeVerbAnswer,
  isImperativeExcluded,
  IMPERATIVE_PRONOUNS,
  NATIVE_TENSE_DIRS,
  getTenseInfo,
} from "../data/verbTasks";
import { loadWeeklySetsByWeek } from "../data/loadWeeklyWords";
import {
  Badge,
  Button,
  Note,
  Panel,
  ScreenHeader,
  Stack,
  TextInput,
} from "../components/ui";

/**
 * Lokaler Weekly-Set-Fallback (wie in Flashcards/MainMenu)
 */
const WEEKLY_KEY_PREFIX = "fluent_weeklyset_";

function getWeeklyKey(languageId, pace, totalWords, profileId, weekIndex = 1) {
  const lang = languageId || "generic";
  const p = pace || "medium";
  const t = totalWords || 0;
  const profile = profileId || "default";
  const w = weekIndex || 1;
  return `${WEEKLY_KEY_PREFIX}${profile}_${lang}_${p}_${t}_w${w}`;
}

function loadWeeklySetLocal(
  languageId,
  pace,
  totalWords,
  profileId,
  weekIndex = 1
) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(
      getWeeklyKey(languageId, pace, totalWords, profileId, weekIndex)
    );
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Array.isArray(data) && data.length > 0) return data;
    return null;
  } catch (e) {
    console.warn("VerbPractice: weekly set load error (local):", e);
    return null;
  }
}

// Personen in fester Reihenfolge, sprachneutrale Schlüssel ("1s" … "3p").
// Die angezeigten Pronomen kommen je Sprache aus languages.js.
const PRONOUNS = PERSON_SLOTS.map((key) => ({ key }));

const PRONOUN_KEYS = PERSON_SLOTS;

function emptyFormInputs() {
  return Object.fromEntries(PERSON_SLOTS.map((key) => [key, ""]));
}

// Dev-Flag: alles im Single-Modus laufen lassen (nur Debug)
const FORCE_SINGLE = false;

// Zufälliger Personen-Index
function getRandomPronounIndex() {
  return Math.floor(Math.random() * PRONOUNS.length);
}

/**
 * Gewichtsfunktion je Form (ohne zeitliche Spaced-Repetition):
 * - Noch nie geübt: hohes Gewicht
 * - Viele Fehler -> höheres Gewicht
 * - Sehr gute Formen -> kleineres Gewicht, aber nie 0
 */
function getPronounWeight(stats) {
  const correct = stats.correct || stats.correctCount || 0;
  const wrong = stats.wrong || stats.wrongCount || 0;
  const total = correct + wrong;

  if (total === 0) {
    return 3;
  }

  const errorRate = total > 0 ? wrong / total : 0;
  const base = 0.3;
  return base + errorRate; // 0.3–1.3 typischer Bereich
}

/**
 * Wählt für ein Verb (verbState) einen Pronomen-Key per Weighted Random.
 */
function chooseNextPronounKeyForVerb(verbState, slots = PRONOUN_KEYS) {
  const forms = (verbState && verbState.forms) || {};
  const keys = slots.length > 0 ? slots : PRONOUN_KEYS;

  const weights = keys.map((key) => {
    const stats = forms[key] || { correct: 0, wrong: 0 };
    return getPronounWeight(stats);
  });

  const totalWeight = weights.reduce((sum, w) => sum + w, 0);

  if (!isFinite(totalWeight) || totalWeight <= 0) {
    return keys[Math.floor(Math.random() * keys.length)];
  }

  const r = Math.random() * totalWeight;
  let cumulative = 0;
  for (let i = 0; i < keys.length; i++) {
    cumulative += weights[i];
    if (r < cumulative) {
      return keys[i];
    }
  }

  return keys[keys.length - 1];
}

// Neuer, leerer Stand für eine Aufgabe (Präsens wie bisher aus ensureVerbStatesForWeeklySet)
function createDefaultVerbState(id, infinitive, translation) {
  return {
    id,
    infinitive,
    translation,
    mode: "table",
    tableAttempts: 0,
    tableGoodRuns: 0,
    lastTableResult: null,
    box: 1,
    singleCorrectTotal: 0,
    correctStreak: 0,
    lastSeenAt: null,
    forms: null,
  };
}

/** Pronomen einer Person in einer Zeitform (Imperativ: tú/usted …, du/Sie …; j' vor Vokal) */
function pronounForTense(langCode, slot, tense, form) {
  if (tense === "imperative") {
    return IMPERATIVE_PRONOUNS[langCode]?.[slot] || pronounFor(langCode, slot);
  }
  return pronounForForm(langCode, slot, form);
}

function pronounKeyToIndex(key) {
  const idx = PRONOUNS.findIndex((p) => p.key === key);
  return idx >= 0 ? idx : getRandomPronounIndex();
}

// Helper: Default-Form-Stats für ein Verb
function createDefaultFormsStats() {
  return Object.fromEntries(
    PERSON_SLOTS.map((key) => [
      key,
      {
        correct: 0,
        wrong: 0,
        correctCount: 0,
        wrongCount: 0,
        everCorrect: false,
      },
    ])
  );
}

export default function VerbPractice({
  onBack,
  language,
  pace,
  profileId,
  weekIndex = 1,
}) {
  const langId = language || "es-de";
  const paceValue = pace || "medium";
  const profile = profileId || "default";
  const effectiveWeekIndex = weekIndex || 1;
  const { source: sourceLang, target: targetLang } = parseLanguageId(langId) || {
    source: "es",
    target: "de",
  };

  const years = PACE_YEARS[paceValue] ?? PACE_YEARS.medium;
  const weeks = Math.round(years * 52);
  const weeklyWords = Math.ceil(TARGET_WORDS / weeks);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Verben des Wochensets, für die es Konjugationen gibt
  const [matchedVerbs, setMatchedVerbs] = useState([]);
  const [verbStates, setVerbStates] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);

  // Tabellenmodus
  const [formInputs, setFormInputs] = useState(emptyFormInputs());
  const [tableResult, setTableResult] = useState(null);

  // Flip-State
  const [isFlipped, setIsFlipped] = useState(false);

  // Einzelmodus
  const [singlePronounIndex, setSinglePronounIndex] = useState(0);
  const [singleInput, setSingleInput] = useState("");
  const [singleResult, setSingleResult] = useState(null);

  // Developer-Infos

  // Tabellen-Lauf abgeschlossen?
  const [hasFinalizedRun, setHasFinalizedRun] = useState(false);

  // 5er-Pause
  const [, setQuestionCount] = useState(0);
  const [showPause, setShowPause] = useState(false);

  // Helper: Fragezähler erhöhen und ggf. Pause anzeigen
  function incrementQuestionCount() {
    setQuestionCount((prev) => {
      const next = prev + 1;
      if (next % 5 === 0) {
        setShowPause(true);
      }
      return next;
    });
  }

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoading(true);
      setError("");
      setMatchedVerbs([]);
      setVerbStates({});
      setCurrentIndex(0);
      setFormInputs(emptyFormInputs());
      setTableResult(null);
      setIsFlipped(false);
      setSinglePronounIndex(0);
      setSingleInput("");
      setSingleResult(null);
      setHasFinalizedRun(false);
      setShowPause(false);
      setQuestionCount(0);

      try {
        // 1) User-ID holen
        let userId = null;
        try {
          const { data: userData, error: userError } =
            await supabase.auth.getUser();
          if (!userError && userData?.user?.id) {
            userId = userData.user.id;
          }
        } catch (e) {
          console.warn("VerbPractice: getUser failed:", e);
        }

        // 2) Weekly-Set laden
        let weeklySet = null;

        if (userId) {
          try {
            const { data, error } = await supabase
              .from("weekly_sets")
              .select("words")
              .eq("user_id", userId)
              .eq("profile_id", profile)
              .eq("language_id", langId)
              .eq("pace", paceValue)
              .eq("total_words", weeklyWords)
              .eq("week_index", effectiveWeekIndex)
              .maybeSingle();

            if (!error && data && Array.isArray(data.words)) {
              weeklySet = data.words;
            } else {
              weeklySet = [];
            }
          } catch (e) {
            console.warn("VerbPractice: remote weekly_sets load failed:", e);
            weeklySet = [];
          }
        } else {
          weeklySet =
            loadWeeklySetLocal(
              langId,
              paceValue,
              weeklyWords,
              profile,
              effectiveWeekIndex
            ) || [];
        }

        if (!Array.isArray(weeklySet)) {
          weeklySet = [];
        }

        console.log("[VerbPractice] WeeklySet size:", weeklySet.length);

        // 3) Konjugationen für Lern- und Muttersprache laden – verknüpft über die Concept-ID.
        // Präsens immer, weitere Zeitformen, sobald sie freigeschaltet sind (tenseSchedule.js)
        const allowedTenses = getAllowedTenses(effectiveWeekIndex, paceValue, sourceLang);
        const tenses = Object.keys(allowedTenses).filter((t) => allowedTenses[t]);
        const nativeDirs = NATIVE_TENSE_DIRS[targetLang] || {};
        const sourceConj = {};
        const targetConj = {};
        await Promise.all(
          tenses.map(async (tense) => {
            const nativeDir = nativeDirs[tense];
            const [src, tgt] = await Promise.all([
              loadConjugations(sourceLang, tense),
              tense === "present" || nativeDir
                ? loadConjugations(targetLang, nativeDir || "present")
                : Promise.resolve(new Map()),
            ]);
            sourceConj[tense] = src;
            targetConj[tense] = tgt;
          })
        );

        // 4) Frühere Wochensets: welche Verben schon bekannt sind (nur für andere Zeitformen nötig)
        let earlierSets = {};
        if (tenses.length > 1 && effectiveWeekIndex > 1) {
          earlierSets = await loadWeeklySetsByWeek({
            langId,
            pace: paceValue,
            weeklyWords,
            profile,
            weekIndex: effectiveWeekIndex - 1,
          });
        }

        // 5) Verb-States holen (Supabase / LocalStorage)
        const existingStates =
          (await loadVerbProgress(langId, profile, effectiveWeekIndex)) || {};

        const {
          verbStates: mergedStatesRaw,
          verbs: verbsFromWeekly,
        } = ensureVerbStatesForWeeklySet(weeklySet, existingStates);

        // 6) Aufgaben der Woche: neue Verben im Präsens + bekannte Verben in anderen Zeitformen
        const verbInfo = new Map();
        const isVerb = (w) => w?.id && (!w.type || w.type === "verb");
        const remember = (w) => {
          if (!isVerb(w) || verbInfo.has(w.id)) return;
          // ältere, gecachte Wochensets haben noch es/de statt source/target
          verbInfo.set(w.id, {
            infinitive: w.source || w.es || "",
            translation: w.target || w.de || "",
          });
        };
        verbsFromWeekly.forEach(remember);
        const learnedByWeek = {};
        for (const [week, words] of Object.entries(earlierSets)) {
          (words || []).forEach(remember);
          learnedByWeek[week] = (words || []).filter(isVerb).map((w) => w.id);
        }

        const isEligible = (verbId, tense) => {
          const forms = sourceConj[tense]?.get(verbId);
          if (!forms || slotsForForms(forms, tense).length === 0) return false;
          if (tense === "present") return true;
          if (tense === "imperative" && isImperativeExcluded(verbId, sourceLang)) return false;
          const native = targetConj[tense]?.get(verbId);
          return !!native && slotsForForms(native, tense).length > 0;
        };

        const tasks = buildVerbTasks({
          weekIndex: effectiveWeekIndex,
          pace: paceValue,
          newVerbIds: verbsFromWeekly.filter(isVerb).map((w) => w.id),
          learnedByWeek,
          isEligible,
          lang: sourceLang,
        });

        for (const task of tasks) {
          if (mergedStatesRaw[task.key]) continue;
          const info = verbInfo.get(task.verbId) || {};
          mergedStatesRaw[task.key] = createDefaultVerbState(
            task.key,
            info.infinitive || "",
            info.translation || ""
          );
        }

        // forms/everCorrect sauber aufbereitet
        const mergedStates = {};
        for (const [id, state] of Object.entries(mergedStatesRaw || {})) {
          const prev = existingStates[id] || {};
          let forms = state.forms || prev.forms;

          if (!forms) {
            forms = createDefaultFormsStats();
          } else {
            const full = createDefaultFormsStats();
            for (const key of PRONOUN_KEYS) {
              if (forms[key]) {
                full[key] = {
                  ...full[key],
                  ...forms[key],
                  everCorrect:
                    forms[key].everCorrect === true ||
                    full[key].everCorrect === true,
                };
              }
            }
            forms = full;
          }

          mergedStates[id] = {
            ...state,
            forms,
          };
        }

        // 7) Aufgaben mit Formen: id = Schlüssel in verb_progress ("es-v-0005@preterite")
        const matched = tasks.map((task) => {
          const info = verbInfo.get(task.verbId) || {};
          const forms = sourceConj[task.tense].get(task.verbId);
          return {
            id: task.key,
            verbId: task.verbId,
            tense: task.tense,
            infinitive: info.infinitive || "",
            translation: info.translation || "",
            forms,
            targetForms: targetConj[task.tense]?.get(task.verbId) || null,
            slots: slotsForForms(forms, task.tense),
          };
        });

        console.log(
          "[VerbPractice] Aufgaben:",
          matched.length,
          matched.filter((m) => m.tense !== "present").map((m) => m.id)
        );

        // Shuffle
        for (let i = matched.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [matched[i], matched[j]] = [matched[j], matched[i]];
        }

        if (cancelled) return;

        if (matched.length === 0) {
          setError(
            "Für diese Woche wurden keine Verben mit Konjugationstabelle gefunden."
          );
        }

        setMatchedVerbs(matched);
        setVerbStates(mergedStates);

        if (matched.length > 0) {
          const firstVerb = matched[0];
          const firstState = mergedStates[firstVerb.id] || {};

          const forms =
            firstState.forms && Object.keys(firstState.forms).length > 0
              ? firstState.forms
              : createDefaultFormsStats();

          const weightedKey = chooseNextPronounKeyForVerb(
            { ...firstState, forms },
            firstVerb.slots
          );
          const idx = pronounKeyToIndex(weightedKey);
          setCurrentIndex(0);
          setSinglePronounIndex(idx);

          // *** NEUE LOGIK: "gesehen" nur über Attempts / Single-Correct ***
          const attempts = firstState.tableAttempts || 0;
          const singleCorrect = firstState.singleCorrectTotal || 0;
          const hasSeenVerb = attempts > 0 || singleCorrect > 0;

          const shouldStartFlipped = FORCE_SINGLE || hasSeenVerb;
          setIsFlipped(shouldStartFlipped);
        }
      } catch (e) {
        console.log("VerbPractice: init exception:", e);
        if (!cancelled) {
          setError(
            "Unerwarteter Fehler beim Laden der Verb-Übung: " +
              (e.message || String(e))
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [
    langId,
    sourceLang,
    targetLang,
    paceValue,
    profile,
    effectiveWeekIndex,
    weeklyWords,
  ]);

  // Autofokus auf erstes Feld im Tabellenmodus nach Flip
  const firstSlot = matchedVerbs[currentIndex]?.slots?.[0] || PERSON_SLOTS[0];
  useEffect(() => {
    if (!isFlipped) return;
    if (typeof document === "undefined") return;

    const t = setTimeout(() => {
      const el = document.getElementById(`verb-input-${firstSlot}`);
      if (el) el.focus();
    }, 350);

    return () => clearTimeout(t);
  }, [isFlipped, currentIndex, firstSlot]);

  // Kein globaler ENTER-Listener – Enter läuft über Inputs (ohne Buttons zu triggern)

  function handleInputChange(key, value) {
    setFormInputs((prev) => ({
      ...prev,
      [key]: value,
    }));
    setHasFinalizedRun(false);
  }

  function evaluateForms(currentVerb) {
    const perPronoun = {};
    let correctCount = 0;
    const totalCount = currentVerb.slots.length;

    currentVerb.slots.forEach((key) => {
      const result = gradeCurrentForm(currentVerb, key, formInputs[key]);
      perPronoun[key] = result;
      if (result.status === "correct" || result.status === "accent") correctCount += 1;
    });

    return {
      correctCount,
      totalCount,
      perPronoun,
    };
  }

  function evaluateSingle(currentVerb, pronounKey, valueRaw) {
    return gradeCurrentForm(currentVerb, pronounKey, valueRaw);
  }

  // Eingabe einer Person bewerten (weibliche Partizipien, j'/Pronomen mit eingetippt: verbTasks.js)
  function gradeCurrentForm(currentVerb, key, valueRaw) {
    return gradeVerbAnswer(
      valueRaw,
      acceptedVerbForms(currentVerb.forms, key, currentVerb.tense, sourceLang),
      {
        lang: sourceLang,
        pronoun: pronounForTense(sourceLang, key, currentVerb.tense, currentVerb.forms[key]),
      }
    );
  }

  function updateTableResultForRow(key, rowResult, slots) {
    setTableResult((prev) => {
      const prevPer = prev?.perPronoun || {};
      const newPer = { ...prevPer, [key]: rowResult };

      let correctCount = 0;
      const totalCount = slots.length;

      slots.forEach((k) => {
        const r = newPer[k];
        if (!r) return;
        if (r.status === "correct" || r.status === "accent") {
          correctCount += 1;
        }
      });

      return {
        perPronoun: newPer,
        correctCount,
        totalCount,
      };
    });
  }

  function handleRowBlur(key) {
    if (!matchedVerbs.length) return;
    const currentVerb = matchedVerbs[currentIndex];
    if (!currentVerb) return;

    const value = formInputs[key] || "";
    const rowResult = evaluateSingle(currentVerb, key, value);
    updateTableResultForRow(key, rowResult, currentVerb.slots);
  }

  function handleRowKeyDown(e, key) {
    if (e.key !== "Enter") return;

    e.preventDefault();
    if (!matchedVerbs.length) return;
    const currentVerb = matchedVerbs[currentIndex];
    if (!currentVerb) return;

    const value = formInputs[key] || "";
    const rowResult = evaluateSingle(currentVerb, key, value);
    updateTableResultForRow(key, rowResult, currentVerb.slots);

    const slots = currentVerb.slots;
    const currentIdx = slots.indexOf(key);

    // Enter: Nur zur nächsten Zeile springen, NICHT "Fertig"/"Nächstes Verb" auslösen
    if (currentIdx >= 0 && currentIdx < slots.length - 1) {
      const nextKey = slots[currentIdx + 1];
      if (typeof document !== "undefined") {
        const nextEl = document.getElementById(`verb-input-${nextKey}`);
        if (nextEl) {
          nextEl.focus();
        }
      }
    }
    // Wenn letzte Zeile: nichts weiter – User muss Button klicken
  }

  function finalizeTableRun() {
    if (!matchedVerbs.length) return;
    const currentVerb = matchedVerbs[currentIndex];
    if (!currentVerb) return;

    const result = evaluateForms(currentVerb);
    setTableResult(result);

    setVerbStates((prev) => {
      const existing = prev[currentVerb.id] || {
        id: currentVerb.id,
        infinitive: currentVerb.infinitive,
        translation: currentVerb.translation || "",
        mode: "table",
        tableAttempts: 0,
        tableGoodRuns: 0,
        lastTableResult: null,
        box: 1,
        singleCorrectTotal: 0,
        correctStreak: 0,
        lastSeenAt: null,
        forms: null,
      };

      // guter Durchlauf: höchstens ein Fehler (bei 6 Personen also 5 richtig)
      const isGoodRun = result.correctCount >= Math.max(1, result.totalCount - 1);
      const newGoodRuns =
        (typeof existing.tableGoodRuns === "number"
          ? existing.tableGoodRuns
          : 0) + (isGoodRun ? 1 : 0);

      const shouldSwitchToSingle =
        existing.mode === "table" && newGoodRuns >= 2;

      const forms = existing.forms || createDefaultFormsStats();

      const newState = {
        ...existing,
        infinitive: currentVerb.infinitive,
        translation: currentVerb.translation || existing.translation || "",
        mode: shouldSwitchToSingle ? "single" : existing.mode || "table",
        tableAttempts: (existing.tableAttempts || 0) + 1,
        tableGoodRuns: newGoodRuns,
        lastTableResult: {
          correct: result.correctCount,
          total: result.totalCount,
        },
        lastSeenAt: new Date().toISOString(),
        forms,
      };

      const updated = {
        ...prev,
        [currentVerb.id]: newState,
      };

      saveVerbProgress(langId, profile, effectiveWeekIndex, updated);
      return updated;
    });

    setHasFinalizedRun(true);
    incrementQuestionCount();
  }

  function handleNextVerb() {
    if (!matchedVerbs.length) return;

    const nextIndex = (currentIndex + 1) % matchedVerbs.length;
    setCurrentIndex(nextIndex);

    const nextVerb = matchedVerbs[nextIndex];
    const stateForNextVerb = verbStates[nextVerb.id] || {
      infinitive: nextVerb.infinitive,
      forms: null,
    };

    const forms = stateForNextVerb.forms || createDefaultFormsStats();

    const weightedKey = chooseNextPronounKeyForVerb(
      { ...stateForNextVerb, forms },
      nextVerb.slots
    );
    const idx = pronounKeyToIndex(weightedKey);

    setFormInputs(emptyFormInputs());
    setTableResult(null);

    // *** NEUE LOGIK: "gesehen" nur über Attempts / Single-Correct ***
    const attempts = stateForNextVerb.tableAttempts || 0;
    const singleCorrect = stateForNextVerb.singleCorrectTotal || 0;
    const hasSeenVerb = attempts > 0 || singleCorrect > 0;

    const shouldStartFlipped = FORCE_SINGLE || hasSeenVerb;
    setIsFlipped(shouldStartFlipped);

    setSinglePronounIndex(idx);
    setSingleInput("");
    setSingleResult(null);
    setHasFinalizedRun(false);
  }

  // ---- Einzelmodus ----

  function handleSingleCheck() {
    if (!matchedVerbs.length) return;
    const currentVerb = matchedVerbs[currentIndex];
    if (!currentVerb) return;

    const pronoun = PRONOUNS[singlePronounIndex];
    const key = pronoun.key;

    const result = evaluateSingle(currentVerb, key, singleInput);
    setSingleResult({ ...result, pronounKey: key });

    const isCorrect =
      result.status === "correct" || result.status === "accent";

    setVerbStates((prev) => {
      const existing = prev[currentVerb.id] || {
        id: currentVerb.id,
        infinitive: currentVerb.infinitive,
        translation: currentVerb.translation || "",
        mode: "single",
        tableAttempts: 0,
        tableGoodRuns: 0,
        lastTableResult: null,
        box: 1,
        singleCorrectTotal: 0,
        correctStreak: 0,
        lastSeenAt: null,
        forms: null,
      };

      let box =
        typeof existing.box === "number" && existing.box >= 1
          ? existing.box
          : 1;
      let correctStreak =
        typeof existing.correctStreak === "number"
          ? existing.correctStreak
          : 0;
      let singleCorrectTotal =
        typeof existing.singleCorrectTotal === "number"
          ? existing.singleCorrectTotal
          : 0;

      const existingForms = existing.forms || createDefaultFormsStats();

      const prevFormState = existingForms[key] || {
        correct: 0,
        wrong: 0,
        correctCount: 0,
        wrongCount: 0,
        everCorrect: false,
      };

      const newFormState = { ...prevFormState };

      if (isCorrect) {
        correctStreak += 1;
        singleCorrectTotal += 1;

        newFormState.correct = (newFormState.correct || 0) + 1;
        newFormState.correctCount =
          (newFormState.correctCount || 0) + 1;
        newFormState.everCorrect = true;

        if (correctStreak >= 3 && box < 5) {
          box += 1;
          correctStreak = 0;
        }
      } else {
        box = Math.max(1, box - 1);
        correctStreak = 0;

        newFormState.wrong = (newFormState.wrong || 0) + 1;
        newFormState.wrongCount =
          (newFormState.wrongCount || 0) + 1;
      }

      const newForms = {
        ...existingForms,
        [key]: newFormState,
      };

      const newState = {
        ...existing,
        infinitive: currentVerb.infinitive,
        translation: currentVerb.translation || existing.translation || "",
        mode: "single",
        box,
        correctStreak,
        singleCorrectTotal,
        lastSeenAt: new Date().toISOString(),
        forms: newForms,
      };

      const updated = {
        ...prev,
        [currentVerb.id]: newState,
      };
      saveVerbProgress(langId, profile, effectiveWeekIndex, updated);
      return updated;
    });

    incrementQuestionCount();
  }

  function getGermanHeaderForPronoun(currentVerb, pronounKey) {
    const verbForm = currentVerb.targetForms?.[pronounKey];
    // Imperativ: die Form enthält schon "Sie"/"wir" ("essen Sie", "iss")
    if (currentVerb.tense === "imperative" && verbForm) {
      const command = verbForm.charAt(0).toUpperCase() + verbForm.slice(1);
      return `${command}!${pronounKey === "3p" ? " (mehrere)" : ""}`;
    }
    // Kopfzeile kurz: "Er ist" statt "Er/sie/es ist"
    const raw = pronounFor(targetLang, pronounKey).split("/")[0];
    const subj = raw.charAt(0).toUpperCase() + raw.slice(1);
    if (!verbForm) {
      return `${subj} ${currentVerb.translation || ""}`.trim();
    }
    return `${subj} ${verbForm}`;
  }

  /* ---------- RENDER ---------- */

  const title = `Verbtraining – Woche ${effectiveWeekIndex}`;
  const backButton = (
    <Button block variant="secondary" onClick={onBack}>
      Zurück zum Hauptmenü
    </Button>
  );

  if (loading) {
    return (
      <>
        <ScreenHeader title={title} />
        <Note>Lade Verben dieser Woche…</Note>
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <ScreenHeader title={title} />
        <Note tone="error">{error}</Note>
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  if (!matchedVerbs.length) {
    return (
      <>
        <ScreenHeader title={title} />
        <Note>Für diese Woche wurden keine passenden Verben gefunden.</Note>
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  const currentVerb = matchedVerbs[currentIndex];
  const stateForVerb = verbStates[currentVerb.id] || null;
  const tenseInfo = getTenseInfo(sourceLang, currentVerb.tense);

  // Tabellenzeilen mit den Pronomen der Lernsprache (Imperativ ohne "yo")
  const pronounRows = currentVerb.slots.map((key) => ({
    key,
    label: pronounForTense(sourceLang, key, currentVerb.tense, currentVerb.forms[key]),
  }));
  const mode = FORCE_SINGLE ? "single" : stateForVerb?.mode || "table";

  // Ergebnis einer Form → Zustand des Eingabefelds (accent = nur Akzent fehlt)
  const inputStatus = (result) =>
    result?.status === "correct"
      ? "correct"
      : result?.status === "accent"
      ? "almost"
      : result?.status === "wrong"
      ? "wrong"
      : undefined;

  // Verb oben als Blickfang (Infinitiv + Übersetzung)
  const verbPrompt = (
    <Panel variant="muted" className="exercise-prompt">
      <Badge tone="blue">
        {tenseInfo.label}
        {tenseInfo.hint && <span className="verb-prompt__hint"> · {tenseInfo.hint}</span>}
      </Badge>
      <div className="exercise-prompt__text verb-prompt">
        {currentVerb.infinitive}
        {currentVerb.translation && (
          <span className="verb-prompt__translation">{currentVerb.translation}</span>
        )}
      </div>
    </Panel>
  );

  const footer = <div className="exercise-actions-tight">{backButton}</div>;

  // ---------- 5er-Pause-Screen ----------
  if (showPause) {
    return (
      <div className="verb-practice-container">
        <ScreenHeader center title="Kurze Pause?" subtitle="Fünf Verben geschafft. Weiter oder später?" />
        <Stack gap={2}>
          <Button
            block
            onClick={() => {
              setShowPause(false);
              handleNextVerb();
            }}
          >
            Weiter
          </Button>
          {backButton}
        </Stack>
      </div>
    );
  }

  // ---------- Einzelmodus ----------
  if (mode === "single") {
    const pronoun = PRONOUNS[singlePronounIndex];
    const header = getGermanHeaderForPronoun(currentVerb, pronoun.key);

    const showCorrection =
      singleResult &&
      (singleResult.status === "wrong" || singleResult.status === "accent");

    const singleDone = !!singleResult;
    const pronounLabel = pronounForTense(
      sourceLang,
      pronoun.key,
      currentVerb.tense,
      currentVerb.forms[pronoun.key]
    );

    return (
      <div className="verb-practice-container">
        <ScreenHeader title={title} subtitle="Setze die passende Form ein" />
        {verbPrompt}

        <Panel key={currentVerb.id} className="verb-single">
          <div className="verb-single__header">{header || "Form üben"}</div>

          <div className="verb-single__row">
            <span className="verb-single__pronoun">{pronounLabel}</span>
            <TextInput
              id="single-input"
              type="text"
              className="verb-single__input"
              value={singleInput}
              autoFocus
              autoComplete="off"
              spellCheck="false"
              status={inputStatus(singleResult)}
              onChange={(e) => setSingleInput(e.target.value)}
            />
            {singleDone && currentVerb.forms[pronoun.key] && (
              <SpeakButton
                small
                text={verbFormForSpeech(currentVerb.forms[pronoun.key], sourceLang, pronoun.key, currentVerb.tense)}
                lang={sourceLang}
                label="Form anhören"
              />
            )}
          </div>

          {showCorrection && (
            <Note tone="error" className="verb-single__correction">
              Richtig: <strong>{singleResult.expected || ""}</strong>
            </Note>
          )}
        </Panel>

        <Button
          block
          className="verb-main-button"
          onClick={singleDone ? handleNextVerb : handleSingleCheck}
        >
          {singleDone ? "Nächstes Verb" : "Fertig"}
        </Button>

        {footer}
      </div>
    );
  }

  // ---------- Tabellenmodus (Flip-Karte) ----------
  return (
    <div className="verb-practice-container">
      <ScreenHeader
        title={title}
        subtitle={isFlipped ? "Trage alle Formen ein" : "Präge dir die Formen ein"}
      />
      {verbPrompt}

      <div className="verb-flip">
        <div
          key={currentVerb.id}
          className={`verb-flip__inner${isFlipped ? " verb-flip__inner--flipped" : ""}`}
        >
          {/* VORDERSEITE */}
          <Panel variant="muted" className="verb-flip__face verb-flip__front">
            <div className="verb-flip__title">Lösungen zum Einprägen</div>

            <div className="verb-table verb-table--read">
              {pronounRows.map(({ key, label }) => (
                <React.Fragment key={key}>
                  <span className="verb-table__pronoun">{label}</span>
                  <span className="verb-table__form">{currentVerb.forms[key] || "—"}</span>
                  {currentVerb.forms[key] ? (
                    <SpeakButton
                      small
                      text={verbFormForSpeech(currentVerb.forms[key], sourceLang, key, currentVerb.tense)}
                      lang={sourceLang}
                      label="Form anhören"
                    />
                  ) : (
                    <span />
                  )}
                </React.Fragment>
              ))}
            </div>
          </Panel>

          {/* RÜCKSEITE */}
          <Panel variant="muted" className="verb-flip__face verb-flip__back">
            <div className="verb-table verb-table--input">
              {pronounRows.map(({ key, label }) => {
                const resultForPronoun = tableResult?.perPronoun?.[key] || null;
                const showCorrection =
                  resultForPronoun &&
                  (resultForPronoun.status === "wrong" ||
                    resultForPronoun.status === "accent");

                return (
                  <React.Fragment key={key}>
                    <span className="verb-table__pronoun">{label}</span>
                    <TextInput
                      id={`verb-input-${key}`}
                      className="verb-table__input"
                      type="text"
                      autoComplete="off"
                      spellCheck="false"
                      value={formInputs[key]}
                      status={inputStatus(resultForPronoun)}
                      onChange={(e) => handleInputChange(key, e.target.value)}
                      onBlur={() => handleRowBlur(key)}
                      onKeyDown={(e) => handleRowKeyDown(e, key)}
                    />
                    <span className="verb-table__correction">
                      {showCorrection ? resultForPronoun.expected || "" : ""}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>

            <div
              className="verb-result"
              style={{ visibility: tableResult ? "visible" : "hidden" }}
            >
              Ergebnis: {tableResult?.correctCount ?? 0}/
              {tableResult?.totalCount ?? currentVerb.slots.length} Formen korrekt
            </div>
          </Panel>
        </div>
      </div>

      {/* Ein fester Button unter der Karte, dreht nicht mit.
          key: bei neuem Verb neu einhängen, damit "Abfragen" wieder den Fokus bekommt */}
      <Button
        key={`${currentVerb.id}-${isFlipped}`}
        block
        className="verb-main-button"
        autoFocus={!isFlipped}
        onClick={
          !isFlipped
            ? () => setIsFlipped(true)
            : hasFinalizedRun
            ? handleNextVerb
            : finalizeTableRun
        }
      >
        {!isFlipped ? "Abfragen" : hasFinalizedRun ? "Nächstes Verb" : "Fertig"}
      </Button>

      {footer}
    </div>
  );
}
